<script>
  import { onMount } from 'svelte';
  import Layout from '../components/Layout.svelte';
  import { settingsApi, userRolesApi, salesTargetsApi, nonWorkingDaysApi } from '../stores/api.js';
  import { _, locale, setLocale, SUPPORTED_LOCALES } from '../lib/i18n.js';
  import { Settings as SettingsIcon, Building, Mail, FileText, Save, Loader, CheckCircle, AlertCircle, Users, Euro, Edit2, X, Check, Globe, Target, TrendingUp, TrendingDown, ChevronDown, Bell, Calendar, Plus, Trash2 } from 'lucide-svelte';

  let activeTab = 'company';
  let loading = true;
  let saving = false;
  let message = null;

  // Company settings
  let company = {
    name: '',
    address: '',
    city: '',
    cap: '',
    province: '',
    country: 'Italia',
    vat: '',
    fiscalCode: '',
    phone: '',
    email: '',
    pec: ''
  };

  // SMTP settings
  let smtp = {
    host: '',
    port: '587',
    user: '',
    password: '',
    fromEmail: '',
    fromName: 'WEBDEPLOY'
  };

  // Email template
  let emailTemplate = '';
  let templateVariables = [];
  let testingSmtp = false;
  let sendingTestEmail = false;

  // Proposal NDA text
  let ndaText = '';

  // Resources (hourly costs)
  let resources = [];
  let loadingResources = false;
  let editingResource = null;
  let editForm = { hourlyCost: '', monthlyHours: '', notes: '' };

  // Sales Targets
  let selectedYear = new Date().getFullYear();
  let salesProducts = [];
  let salesSummary = {
    year: selectedYear,
    totalProducts: 0,
    productsWithTarget: 0,
    totalTargetQuantity: 0,
    totalSoldQuantity: 0,
    progressPercentage: 0,
    productsOnTarget: 0,
    productsBehowTarget: 0
  };
  let modifiedTargets = new Map();
  let loadingSalesTargets = false;

  // ntfy push notifications
  let ntfy = {
    enabled: 'false',
    server_url: 'https://ntfy.inwd.org',
    topic: 'webdeploy-ms',
    username: '',
    password: ''
  };
  let testingNtfy = false;

  // Non-working days (holidays, company closures) and weekend days
  let nonWorkingYear = new Date().getFullYear();
  /** @type {Array<{date: string, label: string, kind: string}>} */
  let nonWorkingDays = [];
  let newNonWorkingDay = { date: '', label: '', kind: 'holiday' };
  /** @type {number[]} 0 = Sunday … 6 = Saturday */
  let weekendDays = [0, 6];
  let loadingNonWorkingDays = false;
  let savingNonWorkingDay = false;
  let deletingNonWorkingDay = null;

  onMount(() => {
    loadSettings();
    loadNonWorkingDays();
  });

  async function loadSettings() {
    loading = true;
    try {
      // Load all settings
      const response = await settingsApi.getAll();
      const settings = response.settings || {};

      // Map company settings
      company = {
        name: settings.company_name || '',
        address: settings.company_address || '',
        city: settings.company_city || '',
        cap: settings.company_cap || '',
        province: settings.company_province || '',
        country: settings.company_country || 'Italia',
        vat: settings.company_vat || '',
        fiscalCode: settings.company_fiscal_code || '',
        phone: settings.company_phone || '',
        email: settings.company_email || '',
        pec: settings.company_pec || ''
      };

      // Map SMTP settings
      smtp = {
        host: settings.smtp_host || '',
        port: settings.smtp_port || '587',
        user: settings.smtp_user || '',
        password: '', // Don't show password
        fromEmail: settings.smtp_from_email || '',
        fromName: settings.smtp_from_name || 'WEBDEPLOY'
      };

      // Load email template
      const templateResponse = await settingsApi.getEmailTemplate();
      emailTemplate = templateResponse.template || '';
      templateVariables = templateResponse.variables || [];

      // Load NDA text
      ndaText = settings.proposal_nda_text || '';

      // Map ntfy settings
      ntfy = {
        enabled: settings.ntfy_enabled || 'false',
        server_url: settings.ntfy_server_url || 'https://ntfy.inwd.org',
        topic: settings.ntfy_topic || 'webdeploy-ms',
        username: settings.ntfy_username || '',
        password: '', // Don't show password
      };

      // Map weekend days (JSON array of weekday numbers, 0 = Sunday). Falls
      // back to Sat/Sun exactly like the backend's own parser, so the UI
      // never disagrees with what pricing.js actually applies.
      try {
        const parsed = settings.weekend_days ? JSON.parse(settings.weekend_days) : null;
        weekendDays = Array.isArray(parsed) && parsed.length > 0 ? parsed : [0, 6];
      } catch (parseErr) {
        weekendDays = [0, 6];
      }

      console.log('[Settings] Loaded settings');
    } catch (err) {
      console.error('[Settings] Error loading:', err);
      message = { type: 'error', text: err.message };
    } finally {
      loading = false;
    }
  }

  async function saveCompanySettings() {
    saving = true;
    message = null;
    try {
      await settingsApi.updateBulk({
        company_name: company.name,
        company_address: company.address,
        company_city: company.city,
        company_cap: company.cap,
        company_province: company.province,
        company_country: company.country,
        company_vat: company.vat,
        company_fiscal_code: company.fiscalCode,
        company_phone: company.phone,
        company_email: company.email,
        company_pec: company.pec
      });
      message = { type: 'success', text: $_('settings.messages.saved') };
    } catch (err) {
      message = { type: 'error', text: err.message };
    } finally {
      saving = false;
    }
  }

  async function saveSmtpSettings() {
    saving = true;
    message = null;
    try {
      const settings = {
        smtp_host: smtp.host,
        smtp_port: smtp.port,
        smtp_user: smtp.user,
        smtp_from_email: smtp.fromEmail,
        smtp_from_name: smtp.fromName
      };

      // Only update password if provided
      if (smtp.password) {
        settings.smtp_password = smtp.password;
      }

      await settingsApi.updateBulk(settings);
      message = { type: 'success', text: $_('settings.messages.saved') };
      smtp.password = ''; // Clear password field
    } catch (err) {
      message = { type: 'error', text: err.message };
    } finally {
      saving = false;
    }
  }

  async function saveEmailTemplate() {
    saving = true;
    message = null;
    try {
      await settingsApi.update('email_template_proposal', emailTemplate);
      message = { type: 'success', text: $_('settings.messages.saved') };
    } catch (err) {
      message = { type: 'error', text: err.message };
    } finally {
      saving = false;
    }
  }

  async function sendTestProposalEmail() {
    sendingTestEmail = true;
    message = null;
    try {
      const result = await settingsApi.sendTestProposalEmail();
      message = { type: 'success', text: $_('settings.templates.sendTestSuccess', { values: { to: result.to } }) };
    } catch (err) {
      message = { type: 'error', text: $_('settings.templates.sendTestError', { values: { error: err.message } }) };
    } finally {
      sendingTestEmail = false;
    }
  }

  async function saveNdaText() {
    saving = true;
    message = null;
    try {
      await settingsApi.update('proposal_nda_text', ndaText);
      message = { type: 'success', text: $_('settings.messages.saved') };
    } catch (err) {
      message = { type: 'error', text: err.message };
    } finally {
      saving = false;
    }
  }

  async function testSmtpConnection() {
    testingSmtp = true;
    message = null;
    try {
      await settingsApi.testSmtp();
      message = { type: 'success', text: $_('settings.smtp.testSuccess') };
    } catch (err) {
      message = { type: 'error', text: $_('settings.smtp.testError', { values: { error: err.message } }) };
    } finally {
      testingSmtp = false;
    }
  }

  async function saveNtfySettings() {
    saving = true;
    message = null;
    try {
      const settings = {
        ntfy_enabled: ntfy.enabled,
        ntfy_server_url: ntfy.server_url,
        ntfy_topic: ntfy.topic,
        ntfy_username: ntfy.username,
      };
      // Only send password if user entered a new one
      if (ntfy.password) {
        settings.ntfy_password = ntfy.password;
      }
      await settingsApi.updateBulk(settings);
      message = { type: 'success', text: $_('settings.messages.saved') };
      ntfy.password = ''; // Clear password field after save
    } catch (err) {
      message = { type: 'error', text: $_('settings.messages.saveError') };
    } finally {
      saving = false;
    }
  }

  async function testNtfyConnection() {
    testingNtfy = true;
    message = null;
    try {
      await settingsApi.testNtfy();
      message = { type: 'success', text: $_('settings.ntfy.testSuccess') };
    } catch (err) {
      message = { type: 'error', text: $_('settings.ntfy.testError', { values: { error: err.message } }) };
    } finally {
      testingNtfy = false;
    }
  }

  async function loadNonWorkingDays() {
    loadingNonWorkingDays = true;
    try {
      nonWorkingDays = await nonWorkingDaysApi.getAll(nonWorkingYear);
    } catch (err) {
      console.error('[Settings] ❌ Non working days load failed', err);
      message = { type: 'error', text: err.message };
    } finally {
      loadingNonWorkingDays = false;
    }
  }

  async function addNonWorkingDay() {
    if (!newNonWorkingDay.date || !newNonWorkingDay.label.trim()) return;
    savingNonWorkingDay = true;
    message = null;
    try {
      await nonWorkingDaysApi.create({ ...newNonWorkingDay, label: newNonWorkingDay.label.trim() });
      newNonWorkingDay = { date: '', label: '', kind: 'holiday' };
      await loadNonWorkingDays();
    } catch (err) {
      console.error('[Settings] ❌ Non working day create failed', err);
      message = { type: 'error', text: err.message };
    } finally {
      savingNonWorkingDay = false;
    }
  }

  /**
   * @param {string} date - YYYY-MM-DD
   */
  async function removeNonWorkingDay(date) {
    deletingNonWorkingDay = date;
    message = null;
    try {
      await nonWorkingDaysApi.delete(date);
      await loadNonWorkingDays();
    } catch (err) {
      console.error('[Settings] ❌ Non working day delete failed', err);
      message = { type: 'error', text: err.message };
    } finally {
      deletingNonWorkingDay = null;
    }
  }

  /**
   * Weekend days ride in the settings table as a JSON array of weekday
   * numbers (0 = Sunday … 6 = Saturday), which is what the pricing service
   * parses. Unticking the last remaining day is refused rather than
   * persisted: `getWeekendDays` in the backend falls back to `[0, 6]` on an
   * empty array, so saving `[]` would silently keep Sat/Sun weekend pricing
   * active while every checkbox in the UI shows unticked.
   * @param {number} day
   */
  async function toggleWeekendDay(day) {
    const next = weekendDays.includes(day)
      ? weekendDays.filter((entry) => entry !== day)
      : [...weekendDays, day];

    if (next.length === 0) {
      message = { type: 'error', text: $_('settings.nonWorkingDays.weekendEmptyError') };
      return;
    }

    const sorted = [...next].sort((a, b) => a - b);
    weekendDays = sorted;
    message = null;
    try {
      await settingsApi.update('weekend_days', JSON.stringify(sorted));
    } catch (err) {
      console.error('[Settings] ❌ Weekend days update failed', err);
      message = { type: 'error', text: err.message };
    }
  }

  async function loadResources() {
    loadingResources = true;
    try {
      console.log('[Settings] Loading resources...');
      const response = await userRolesApi.getResources();
      resources = response.users || [];
      console.log(`[Settings] Loaded ${resources.length} resources`);
    } catch (err) {
      console.error('[Settings] Error loading resources:', err);
      message = { type: 'error', text: err.message };
    } finally {
      loadingResources = false;
    }
  }

  function startEditResource(resource) {
    editingResource = resource.userId;
    editForm = {
      hourlyCost: resource.hourlyCost ?? '',
      monthlyHours: resource.monthlyHours ?? '',
      notes: resource.notes ?? ''
    };
  }

  function cancelEditResource() {
    editingResource = null;
    editForm = { hourlyCost: '', monthlyHours: '', notes: '' };
  }

  async function saveResourceCost(userId) {
    saving = true;
    message = null;
    try {
      console.log(`[Settings] Saving resource cost for ${userId}:`, editForm);
      await userRolesApi.updateResourceCost(
        userId,
        parseFloat(editForm.hourlyCost) || 0,
        editForm.monthlyHours !== '' ? parseFloat(editForm.monthlyHours) : null,
        editForm.notes || null
      );

      // Update local state
      const idx = resources.findIndex(r => r.userId === userId);
      if (idx !== -1) {
        resources[idx].hourlyCost = parseFloat(editForm.hourlyCost) || 0;
        resources[idx].monthlyHours = editForm.monthlyHours !== '' ? parseFloat(editForm.monthlyHours) : null;
        resources[idx].notes = editForm.notes || null;
        resources = [...resources];
      }

      editingResource = null;
      editForm = { hourlyCost: '', monthlyHours: '', notes: '' };
      message = { type: 'success', text: $_('settings.messages.saved') };
    } catch (err) {
      console.error('[Settings] Error saving resource cost:', err);
      message = { type: 'error', text: err.message };
    } finally {
      saving = false;
    }
  }

  function getRoleLabel(role) {
    const labels = {
      'admin': $_('settings.resources.roles.admin'),
      'sales': $_('settings.resources.roles.sales'),
      'administrative-office': $_('settings.resources.roles.administrativeOffice')
    };
    return labels[role] || role;
  }

  // Load resources when switching to resources tab
  $: if (activeTab === 'resources' && resources.length === 0 && !loadingResources) {
    loadResources();
  }

  // Load sales targets when switching to sales-targets tab
  $: if (activeTab === 'sales-targets' && salesProducts.length === 0 && !loadingSalesTargets) {
    loadSalesTargets();
  }

  // Year options for sales targets
  $: yearOptions = [
    selectedYear + 1,
    selectedYear,
    selectedYear - 1,
    selectedYear - 2
  ].sort((a, b) => b - a);

  async function loadSalesTargets() {
    loadingSalesTargets = true;
    modifiedTargets.clear();
    try {
      console.log(`[Settings] Loading sales targets for year ${selectedYear}`);
      const response = await salesTargetsApi.getProgress(selectedYear);
      salesProducts = response.products || [];
      salesSummary = response.summary || {
        year: selectedYear,
        totalProducts: 0,
        productsWithTarget: 0,
        totalTargetQuantity: 0,
        totalSoldQuantity: 0,
        progressPercentage: 0,
        productsOnTarget: 0,
        productsBehowTarget: 0
      };
      console.log(`[Settings] Loaded ${salesProducts.length} products for sales targets`);
    } catch (err) {
      console.error('[Settings] Error loading sales targets:', err);
      message = { type: 'error', text: err.message };
    } finally {
      loadingSalesTargets = false;
    }
  }

  function handleYearChange(event) {
    selectedYear = parseInt(event.target.value);
    loadSalesTargets();
  }

  function handleTargetChange(productId, value) {
    const numValue = parseFloat(value) || 0;
    modifiedTargets.set(productId, numValue);
    modifiedTargets = modifiedTargets;
  }

  async function saveSalesTargets() {
    if (modifiedTargets.size === 0) {
      message = { type: 'info', text: $_('reports.noChanges') };
      return;
    }

    saving = true;
    message = null;
    try {
      const targets = Array.from(modifiedTargets.entries()).map(([productId, targetQuantity]) => ({
        productId,
        targetQuantity
      }));

      console.log(`[Settings] Saving ${targets.length} targets for year ${selectedYear}`);
      await salesTargetsApi.bulkUpdate(selectedYear, targets);

      message = { type: 'success', text: $_('reports.saved') };
      modifiedTargets.clear();
      modifiedTargets = modifiedTargets;

      await loadSalesTargets();
    } catch (err) {
      console.error('[Settings] Error saving targets:', err);
      message = { type: 'error', text: err.message };
    } finally {
      saving = false;
    }
  }

  function getProgressColor(target, sold) {
    if (target <= 0) return 'bg-gray-200 dark:bg-gray-700';
    const percentage = (sold / target) * 100;
    if (percentage >= 100) return 'bg-green-500';
    if (percentage >= 75) return 'bg-yellow-500';
    if (percentage >= 50) return 'bg-orange-500';
    return 'bg-red-500';
  }

  function getProgressPercentage(target, sold) {
    if (target <= 0) return 0;
    return Math.min(Math.round((sold / target) * 100), 100);
  }

  // Language labels for display
  const languageLabels = {
    'en': 'English',
    'it': 'Italiano'
  };

  // Language flags for visual identification
  const languageFlags = {
    'en': '🇬🇧',
    'it': '🇮🇹'
  };

  const tabs = [
    { id: 'company', label: $_('settings.tabs.company'), icon: Building },
    { id: 'email', label: $_('settings.tabs.email'), icon: Mail },
    { id: 'templates', label: $_('settings.tabs.templates'), icon: FileText },
    { id: 'resources', label: $_('settings.tabs.resources'), icon: Users },
    { id: 'sales-targets', label: $_('settings.tabs.salesTargets'), icon: Target },
    { id: 'notifications', label: $_('settings.tabs.notifications'), icon: Bell },
    { id: 'non-working-days', label: $_('settings.tabs.nonWorkingDays'), icon: Calendar },
    { id: 'preferences', label: $_('settings.tabs.preferences'), icon: Globe }
  ];
</script>

<Layout>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center space-x-4">
      <div class="p-3 bg-linear-to-br from-primary-500 to-primary-600 rounded-2xl shadow-lg">
        <SettingsIcon size={28} class="text-white" />
      </div>
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">{$_('settings.title')}</h1>
        <p class="text-sm text-gray-500 dark:text-gray-400">{$_('settings.subtitle')}</p>
      </div>
    </div>

    <!-- Message -->
    {#if message}
      <div class="p-4 rounded-lg flex items-center space-x-2 {message.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'}">
        {#if message.type === 'success'}
          <CheckCircle size={20} />
        {:else}
          <AlertCircle size={20} />
        {/if}
        <span>{message.text}</span>
        <button class="ml-auto hover:opacity-70" onclick={() => message = null}>&times;</button>
      </div>
    {/if}

    {#if loading}
      <div class="card p-12 text-center">
        <Loader size={48} class="mx-auto text-primary-500 animate-spin mb-4" />
        <p class="text-gray-500 dark:text-gray-400">{$_('common.loading')}</p>
      </div>
    {:else}
      <!-- Tabs -->
      <div class="border-b border-gray-200 dark:border-gray-700">
        <nav class="flex space-x-8">
          {#each tabs as tab}
            <button
              class="py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors {activeTab === tab.id ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'}"
              onclick={() => activeTab = tab.id}
            >
              <svelte:component this={tab.icon} size={18} />
              <span>{tab.label}</span>
            </button>
          {/each}
        </nav>
      </div>

      <!-- Company Tab -->
      {#if activeTab === 'company'}
        <div class="card p-6">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">{$_('settings.company.title')}</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">{$_('settings.company.subtitle')}</p>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="label">{$_('settings.company.name')}</label>
              <input type="text" class="input w-full" bind:value={company.name} />
            </div>
            <div>
              <label class="label">{$_('settings.company.vat')}</label>
              <input type="text" class="input w-full" bind:value={company.vat} />
            </div>
            <div>
              <label class="label">{$_('settings.company.fiscalCode')}</label>
              <input type="text" class="input w-full" bind:value={company.fiscalCode} />
            </div>
            <div>
              <label class="label">{$_('settings.company.address')}</label>
              <input type="text" class="input w-full" bind:value={company.address} />
            </div>
            <div>
              <label class="label">{$_('settings.company.city')}</label>
              <input type="text" class="input w-full" bind:value={company.city} />
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="label">{$_('settings.company.cap')}</label>
                <input type="text" class="input w-full" bind:value={company.cap} />
              </div>
              <div>
                <label class="label">{$_('settings.company.province')}</label>
                <input type="text" class="input w-full" bind:value={company.province} />
              </div>
            </div>
            <div>
              <label class="label">{$_('settings.company.country')}</label>
              <input type="text" class="input w-full" bind:value={company.country} />
            </div>
            <div>
              <label class="label">{$_('settings.company.phone')}</label>
              <input type="tel" class="input w-full" bind:value={company.phone} />
            </div>
            <div>
              <label class="label">{$_('settings.company.email')}</label>
              <input type="email" class="input w-full" bind:value={company.email} />
            </div>
            <div>
              <label class="label">{$_('settings.company.pec')}</label>
              <input type="email" class="input w-full" bind:value={company.pec} />
            </div>
          </div>

          <div class="mt-6 flex justify-end">
            <button
              class="btn btn-primary flex items-center space-x-2"
              onclick={saveCompanySettings}
              disabled={saving}
            >
              {#if saving}
                <Loader size={18} class="animate-spin" />
              {:else}
                <Save size={18} />
              {/if}
              <span>{$_('common.save')}</span>
            </button>
          </div>
        </div>
      {/if}

      <!-- Email Tab -->
      {#if activeTab === 'email'}
        <div class="card p-6">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">{$_('settings.smtp.title')}</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">{$_('settings.smtp.subtitle')}</p>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="label">{$_('settings.smtp.host')}</label>
              <input type="text" class="input w-full" bind:value={smtp.host} placeholder={$_('settings.smtp.hostPlaceholder')} />
            </div>
            <div>
              <label class="label">{$_('settings.smtp.port')}</label>
              <input type="text" class="input w-full" bind:value={smtp.port} placeholder={$_('settings.smtp.portPlaceholder')} />
            </div>
            <div>
              <label class="label">{$_('settings.smtp.user')}</label>
              <input type="text" class="input w-full" bind:value={smtp.user} placeholder={$_('settings.smtp.userPlaceholder')} />
            </div>
            <div>
              <label class="label">{$_('settings.smtp.password')}</label>
              <input type="password" class="input w-full" bind:value={smtp.password} placeholder={$_('settings.smtp.passwordPlaceholder')} />
            </div>
            <div>
              <label class="label">{$_('settings.smtp.fromEmail')}</label>
              <input type="email" class="input w-full" bind:value={smtp.fromEmail} placeholder={$_('settings.smtp.fromEmailPlaceholder')} />
            </div>
            <div>
              <label class="label">{$_('settings.smtp.fromName')}</label>
              <input type="text" class="input w-full" bind:value={smtp.fromName} placeholder={$_('settings.smtp.fromNamePlaceholder')} />
            </div>
          </div>

          <div class="mt-6 flex justify-between">
            <button
              class="btn btn-secondary flex items-center space-x-2"
              onclick={testSmtpConnection}
              disabled={testingSmtp || !smtp.host}
            >
              {#if testingSmtp}
                <Loader size={18} class="animate-spin" />
                <span>{$_('settings.smtp.testing')}</span>
              {:else}
                <Mail size={18} />
                <span>{$_('settings.smtp.test')}</span>
              {/if}
            </button>

            <button
              class="btn btn-primary flex items-center space-x-2"
              onclick={saveSmtpSettings}
              disabled={saving}
            >
              {#if saving}
                <Loader size={18} class="animate-spin" />
              {:else}
                <Save size={18} />
              {/if}
              <span>{$_('common.save')}</span>
            </button>
          </div>
        </div>
      {/if}

      <!-- Templates Tab -->
      {#if activeTab === 'templates'}
        <!-- NDA Text Section -->
        <div class="card p-6 mb-6">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">{$_('settings.templates.nda')}</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">{$_('settings.templates.ndaSubtitle')}</p>

          <div class="mb-6">
            <label class="label">{$_('settings.templates.ndaLabel')}</label>
            <textarea
              class="input w-full h-32 text-sm"
              bind:value={ndaText}
              placeholder={$_('settings.templates.ndaPlaceholder')}
            ></textarea>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-2">{$_('settings.templates.ndaHint')}</p>
          </div>

          <div class="flex justify-end">
            <button
              class="btn btn-primary flex items-center space-x-2"
              onclick={saveNdaText}
              disabled={saving}
            >
              {#if saving}
                <Loader size={18} class="animate-spin" />
              {:else}
                <Save size={18} />
              {/if}
              <span>{$_('common.save')}</span>
            </button>
          </div>
        </div>

        <!-- Email Template Section -->
        <div class="card p-6">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">{$_('settings.templates.title')}</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">{$_('settings.templates.subtitle')}</p>

          <div class="mb-6">
            <label class="label">{$_('settings.templates.quotation')}</label>
            <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">{$_('settings.templates.quotationSubtitle')}</p>
            <textarea
              class="input w-full h-48 font-mono text-sm"
              bind:value={emailTemplate}
            ></textarea>
          </div>

          <div class="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
            <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{$_('settings.templates.variables')}</h4>
            <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">{$_('settings.templates.variableDescription')}</p>
            <div class="flex flex-wrap gap-2">
              {#each templateVariables as variable}
                <code class="px-2 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded text-xs text-gray-700 dark:text-gray-300">
                  {'{' + variable.key + '}'} - {variable.description}
                </code>
              {/each}
            </div>
          </div>

          <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">{$_('settings.templates.sendTestHint')}</p>

          <div class="flex justify-between">
            <button
              class="btn btn-secondary flex items-center space-x-2"
              onclick={sendTestProposalEmail}
              disabled={sendingTestEmail}
            >
              {#if sendingTestEmail}
                <Loader size={18} class="animate-spin" />
                <span>{$_('settings.templates.sendingTest')}</span>
              {:else}
                <Mail size={18} />
                <span>{$_('settings.templates.sendTest')}</span>
              {/if}
            </button>

            <button
              class="btn btn-primary flex items-center space-x-2"
              onclick={saveEmailTemplate}
              disabled={saving}
            >
              {#if saving}
                <Loader size={18} class="animate-spin" />
              {:else}
                <Save size={18} />
              {/if}
              <span>{$_('common.save')}</span>
            </button>
          </div>
        </div>
      {/if}

      <!-- Resources Tab -->
      {#if activeTab === 'resources'}
        <div class="card p-6">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">{$_('settings.resources.title')}</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">{$_('settings.resources.subtitle')}</p>

          {#if loadingResources}
            <div class="text-center py-8">
              <Loader size={32} class="mx-auto text-primary-500 animate-spin mb-4" />
              <p class="text-gray-500 dark:text-gray-400">{$_('common.loading')}</p>
            </div>
          {:else if resources.length === 0}
            <div class="text-center py-8">
              <Users size={48} class="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
              <p class="text-gray-500 dark:text-gray-400">{$_('settings.resources.noResources')}</p>
            </div>
          {:else}
            <div class="overflow-x-auto">
              <table class="w-full">
                <thead>
                  <tr class="text-left text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                    <th class="pb-3 pr-4">{$_('settings.resources.email')}</th>
                    <th class="pb-3 pr-4">{$_('settings.resources.rolesColumn')}</th>
                    <th class="pb-3 pr-4">{$_('settings.resources.hourlyCost')}</th>
                    <th class="pb-3 pr-4">{$_('settings.resources.monthlyHours')}</th>
                    <th class="pb-3 pr-4">{$_('settings.resources.notes')}</th>
                    <th class="pb-3 text-right">{$_('common.actions')}</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                  {#each resources as resource}
                    <tr class="text-sm">
                      <td class="py-3 pr-4">
                        <span class="font-medium text-gray-900 dark:text-white">{resource.email || resource.userId}</span>
                      </td>
                      <td class="py-3 pr-4">
                        <div class="flex flex-wrap gap-1">
                          {#each resource.roles as role}
                            <span class="px-2 py-0.5 text-xs rounded-full {role === 'admin' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' : role === 'sales' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'}">
                              {getRoleLabel(role)}
                            </span>
                          {/each}
                        </div>
                      </td>
                      {#if editingResource === resource.userId}
                        <td class="py-3 pr-4">
                          <div class="flex items-center gap-1">
                            <Euro size={14} class="text-gray-400" />
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              class="input w-24 py-1 text-sm"
                              bind:value={editForm.hourlyCost}
                              placeholder="0.00"
                            />
                          </div>
                        </td>
                        <td class="py-3 pr-4">
                          <input
                            type="number"
                            step="0.5"
                            min="0"
                            class="input w-24 py-1 text-sm"
                            bind:value={editForm.monthlyHours}
                            placeholder={$_('settings.resources.monthlyHoursPlaceholder')}
                          />
                        </td>
                        <td class="py-3 pr-4">
                          <input
                            type="text"
                            class="input w-full py-1 text-sm"
                            bind:value={editForm.notes}
                            placeholder={$_('settings.resources.notesPlaceholder')}
                          />
                        </td>
                        <td class="py-3 text-right">
                          <div class="flex items-center justify-end gap-1">
                            <button
                              class="p-1.5 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                              onclick={() => saveResourceCost(resource.userId)}
                              disabled={saving}
                              title={$_('common.save')}
                            >
                              {#if saving}
                                <Loader size={16} class="animate-spin" />
                              {:else}
                                <Check size={16} />
                              {/if}
                            </button>
                            <button
                              class="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                              onclick={cancelEditResource}
                              title={$_('common.cancel')}
                            >
                              <X size={16} />
                            </button>
                          </div>
                        </td>
                      {:else}
                        <td class="py-3 pr-4">
                          {#if resource.hourlyCost != null}
                            <span class="font-medium text-gray-900 dark:text-white">€ {resource.hourlyCost.toFixed(2)}</span>
                          {:else}
                            <span class="text-gray-400 dark:text-gray-500">-</span>
                          {/if}
                        </td>
                        <td class="py-3 pr-4">
                          {#if resource.monthlyHours != null}
                            <span class="font-medium text-gray-900 dark:text-white">{resource.monthlyHours}h</span>
                          {:else}
                            <span class="text-gray-400 dark:text-gray-500">-</span>
                          {/if}
                        </td>
                        <td class="py-3 pr-4">
                          <span class="text-gray-600 dark:text-gray-300 text-sm truncate max-w-xs block">{resource.notes || '-'}</span>
                        </td>
                        <td class="py-3 text-right">
                          <button
                            class="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
                            onclick={() => startEditResource(resource)}
                            title={$_('common.edit')}
                          >
                            <Edit2 size={16} />
                          </button>
                        </td>
                      {/if}
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          {/if}
        </div>
      {/if}

      <!-- Sales Targets Tab -->
      {#if activeTab === 'sales-targets'}
        <div class="space-y-6">
          <!-- Year Selector -->
          <div class="flex justify-end">
            <div class="relative">
              <select
                class="appearance-none bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 pr-10 text-sm font-medium text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                value={selectedYear}
                onchange={handleYearChange}
              >
                {#each yearOptions as year}
                  <option value={year}>{year}</option>
                {/each}
              </select>
              <ChevronDown size={16} class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {#if loadingSalesTargets}
            <div class="card p-12 text-center">
              <Loader size={48} class="mx-auto text-primary-500 animate-spin mb-4" />
              <p class="text-gray-500 dark:text-gray-400">{$_('common.loading')}</p>
            </div>
          {:else}
            <!-- Summary Cards -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
              <!-- Overall Progress -->
              <div class="card p-6">
                <div class="flex items-center justify-between mb-4">
                  <span class="text-sm font-medium text-gray-500 dark:text-gray-400">{$_('reports.overallProgress')}</span>
                  <Target size={20} class="text-primary-500" />
                </div>
                <div class="flex items-end space-x-2">
                  <span class="text-3xl font-bold text-gray-900 dark:text-white">{salesSummary.progressPercentage}%</span>
                </div>
                <div class="mt-4 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    class="h-full bg-linear-to-r from-primary-500 to-accent-500 transition-all duration-500"
                    style="width: {Math.min(salesSummary.progressPercentage, 100)}%"
                  ></div>
                </div>
                <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {salesSummary.totalSoldQuantity.toLocaleString()} / {salesSummary.totalTargetQuantity.toLocaleString()}
                </p>
              </div>

              <!-- Products On Target -->
              <div class="card p-6">
                <div class="flex items-center justify-between mb-4">
                  <span class="text-sm font-medium text-gray-500 dark:text-gray-400">{$_('reports.onTarget')}</span>
                  <TrendingUp size={20} class="text-green-500" />
                </div>
                <div class="flex items-end space-x-2">
                  <span class="text-3xl font-bold text-green-600 dark:text-green-400">{salesSummary.productsOnTarget}</span>
                  <span class="text-lg text-gray-500 dark:text-gray-400 pb-1">/ {salesSummary.productsWithTarget}</span>
                </div>
                <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">{$_('reports.productsOnTarget')}</p>
              </div>

              <!-- Products Below Target -->
              <div class="card p-6">
                <div class="flex items-center justify-between mb-4">
                  <span class="text-sm font-medium text-gray-500 dark:text-gray-400">{$_('reports.behindTarget')}</span>
                  <TrendingDown size={20} class="text-red-500" />
                </div>
                <div class="flex items-end space-x-2">
                  <span class="text-3xl font-bold text-red-600 dark:text-red-400">{salesSummary.productsBehowTarget}</span>
                  <span class="text-lg text-gray-500 dark:text-gray-400 pb-1">/ {salesSummary.productsWithTarget}</span>
                </div>
                <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">{$_('reports.productsBehind')}</p>
              </div>
            </div>

            <!-- Products Table -->
            <div class="card overflow-hidden">
              <div class="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{$_('reports.salesTargets')}</h3>
                <button
                  class="btn btn-primary flex items-center space-x-2"
                  onclick={saveSalesTargets}
                  disabled={saving || modifiedTargets.size === 0}
                >
                  {#if saving}
                    <Loader size={18} class="animate-spin" />
                  {:else}
                    <Save size={18} />
                  {/if}
                  <span>{$_('common.save')}</span>
                  {#if modifiedTargets.size > 0}
                    <span class="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">{modifiedTargets.size}</span>
                  {/if}
                </button>
              </div>

              <div class="overflow-x-auto">
                <table class="w-full">
                  <thead class="bg-gray-50 dark:bg-gray-800/50">
                    <tr>
                      <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('reports.product')}</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('reports.category')}</th>
                      <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('reports.target')}</th>
                      <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('reports.sold')}</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('reports.progress')}</th>
                      <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('reports.status')}</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                    {#each salesProducts as product (product.product_id)}
                      {@const target = modifiedTargets.has(product.product_id) ? modifiedTargets.get(product.product_id) : parseFloat(product.target_quantity) || 0}
                      {@const sold = parseFloat(product.sold_quantity) || 0}
                      {@const percentage = getProgressPercentage(target, sold)}
                      {@const isModified = modifiedTargets.has(product.product_id)}
                      <tr class="hover:bg-gray-50 dark:hover:bg-gray-800/50 {isModified ? 'bg-yellow-50 dark:bg-yellow-900/10' : ''}">
                        <td class="px-4 py-4">
                          <div class="font-medium text-gray-900 dark:text-white">{product.product_name}</div>
                          <div class="text-sm text-gray-500 dark:text-gray-400">{product.product_code}</div>
                        </td>
                        <td class="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">
                          {product.product_category || '-'}
                        </td>
                        <td class="px-4 py-4 text-right">
                          <input
                            type="number"
                            class="w-24 px-2 py-1 text-right text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 {isModified ? 'border-yellow-500 dark:border-yellow-500' : ''}"
                            value={target}
                            min="0"
                            step="1"
                            oninput={(e) => handleTargetChange(product.product_id, e.target.value)}
                          />
                        </td>
                        <td class="px-4 py-4 text-right text-sm font-medium text-gray-900 dark:text-white">
                          {sold.toLocaleString()}
                        </td>
                        <td class="px-4 py-4">
                          <div class="flex items-center space-x-3">
                            <div class="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                              <div
                                class="h-full transition-all duration-300 {getProgressColor(target, sold)}"
                                style="width: {percentage}%"
                              ></div>
                            </div>
                            <span class="text-sm font-medium text-gray-600 dark:text-gray-300 w-12 text-right">{percentage}%</span>
                          </div>
                        </td>
                        <td class="px-4 py-4 text-center">
                          {#if target <= 0}
                            <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                              {$_('reports.noTarget')}
                            </span>
                          {:else if sold >= target}
                            <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                              <CheckCircle size={14} class="mr-1" />
                              {$_('reports.achieved')}
                            </span>
                          {:else}
                            <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300">
                              <TrendingDown size={14} class="mr-1" />
                              {$_('reports.behind')}
                            </span>
                          {/if}
                        </td>
                      </tr>
                    {:else}
                      <tr>
                        <td colspan="6" class="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
                          {$_('reports.noProducts')}
                        </td>
                      </tr>
                    {/each}
                  </tbody>
                </table>
              </div>
            </div>
          {/if}
        </div>
      {/if}

      <!-- Notifications Tab -->
      {#if activeTab === 'notifications'}
        <div class="card p-6">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">{$_('settings.ntfy.title')}</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">{$_('settings.ntfy.subtitle')}</p>

          <form onsubmit={(e) => { e.preventDefault(); saveNtfySettings(); }} class="space-y-4">
            <!-- Enable/Disable toggle -->
            <div class="mb-6">
              <label class="label">{$_('settings.ntfy.enabled')}</label>
              <div class="flex gap-3 mt-1">
                <button
                  type="button"
                  class="flex items-center gap-2 px-4 py-2 border-2 transition-all
                    {ntfy.enabled === 'true'
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                      : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'
                    }"
                  onclick={() => ntfy.enabled = 'true'}
                >
                  <CheckCircle size={16} />
                  {$_('settings.ntfy.enabledOn')}
                </button>
                <button
                  type="button"
                  class="flex items-center gap-2 px-4 py-2 border-2 transition-all
                    {ntfy.enabled !== 'true'
                      ? 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                      : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'
                    }"
                  onclick={() => ntfy.enabled = 'false'}
                >
                  <X size={16} />
                  {$_('settings.ntfy.enabledOff')}
                </button>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label for="ntfy-server" class="label">{$_('settings.ntfy.serverUrl')}</label>
                <input id="ntfy-server" type="url" class="input" bind:value={ntfy.server_url} placeholder={$_('settings.ntfy.serverUrlPlaceholder')} />
              </div>
              <div>
                <label for="ntfy-topic" class="label">{$_('settings.ntfy.topic')}</label>
                <input id="ntfy-topic" type="text" class="input" bind:value={ntfy.topic} placeholder={$_('settings.ntfy.topicPlaceholder')} />
              </div>
              <div>
                <label for="ntfy-user" class="label">{$_('settings.ntfy.username')}</label>
                <input id="ntfy-user" type="text" class="input" bind:value={ntfy.username} placeholder={$_('settings.ntfy.usernamePlaceholder')} />
              </div>
              <div>
                <label for="ntfy-pass" class="label">{$_('settings.ntfy.password')}</label>
                <input id="ntfy-pass" type="password" class="input" bind:value={ntfy.password} placeholder={$_('settings.ntfy.passwordPlaceholder')} />
              </div>
            </div>

            <!-- Events info -->
            <div class="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-sm text-blue-700 dark:text-blue-300">
              <p class="font-semibold mb-2">{$_('settings.ntfy.eventsTitle')}</p>
              <ul class="list-disc list-inside space-y-1">
                <li>{$_('settings.ntfy.eventCreated')}</li>
                <li>{$_('settings.ntfy.eventSent')}</li>
                <li>{$_('settings.ntfy.eventSeen')}</li>
                <li>{$_('settings.ntfy.eventApproved')}</li>
                <li>{$_('settings.ntfy.eventConfirmed')}</li>
                <li>{$_('settings.ntfy.eventCancelled')}</li>
                <li>{$_('settings.ntfy.eventSigned')}</li>
              </ul>
            </div>

            <div class="flex items-center gap-3 pt-2">
              <button type="submit" class="btn-primary flex items-center gap-2" disabled={saving}>
                {#if saving}
                  <Loader size={16} class="animate-spin" />
                {:else}
                  <Save size={16} />
                {/if}
                {$_('common.save')}
              </button>
              <button type="button" class="btn-secondary flex items-center gap-2" disabled={testingNtfy} onclick={testNtfyConnection}>
                {#if testingNtfy}
                  <Loader size={16} class="animate-spin" />
                  {$_('settings.ntfy.testing')}
                {:else}
                  <Bell size={16} />
                  {$_('settings.ntfy.test')}
                {/if}
              </button>
            </div>
          </form>
        </div>
      {/if}

      <!-- Non-working days Tab -->
      {#if activeTab === 'non-working-days'}
        <div class="space-y-6">
          <!-- Weekend Days -->
          <div class="card p-6">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">{$_('settings.nonWorkingDays.weekendDays')}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">{$_('settings.nonWorkingDays.weekendHint')}</p>
            <div class="flex flex-wrap gap-2">
              {#each [0, 1, 2, 3, 4, 5, 6] as day}
                <button
                  type="button"
                  class="px-3 py-1.5 rounded-lg text-sm font-medium border-2 transition-all
                    {weekendDays.includes(day)
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                      : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'}"
                  onclick={() => toggleWeekendDay(day)}
                >
                  {$_(`settings.nonWorkingDays.days.${day}`)}
                </button>
              {/each}
            </div>
          </div>

          <!-- Non-working days list -->
          <div class="card p-6">
            <div class="flex items-center justify-between mb-2">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{$_('settings.nonWorkingDays.title')}</h3>
              <div class="w-28">
                <input
                  type="number"
                  bind:value={nonWorkingYear}
                  onchange={loadNonWorkingDays}
                  aria-label={$_('settings.nonWorkingDays.year')}
                  class="input w-full text-center"
                />
              </div>
            </div>
            <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">{$_('settings.nonWorkingDays.hint')}</p>

            <!-- Add form -->
            <div class="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
              <div>
                <label class="label">{$_('settings.nonWorkingDays.date')}</label>
                <input type="date" bind:value={newNonWorkingDay.date} class="input w-full" disabled={savingNonWorkingDay} />
              </div>
              <div>
                <label class="label">{$_('settings.nonWorkingDays.label')}</label>
                <input type="text" bind:value={newNonWorkingDay.label} placeholder={$_('settings.nonWorkingDays.label')} class="input w-full" disabled={savingNonWorkingDay} />
              </div>
              <div>
                <label class="label">{$_('settings.nonWorkingDays.kind')}</label>
                <select bind:value={newNonWorkingDay.kind} class="input w-full" disabled={savingNonWorkingDay}>
                  <option value="holiday">{$_('settings.nonWorkingDays.kinds.holiday')}</option>
                  <option value="closure">{$_('settings.nonWorkingDays.kinds.closure')}</option>
                </select>
              </div>
              <div class="flex items-end">
                <button
                  type="button"
                  class="btn btn-primary w-full flex items-center justify-center space-x-2"
                  onclick={addNonWorkingDay}
                  disabled={savingNonWorkingDay || !newNonWorkingDay.date || !newNonWorkingDay.label.trim()}
                >
                  {#if savingNonWorkingDay}
                    <Loader size={16} class="animate-spin" />
                  {:else}
                    <Plus size={16} />
                  {/if}
                  <span>{$_('settings.nonWorkingDays.add')}</span>
                </button>
              </div>
            </div>

            {#if loadingNonWorkingDays}
              <div class="text-center py-8">
                <Loader size={32} class="mx-auto text-primary-500 animate-spin mb-4" />
                <p class="text-gray-500 dark:text-gray-400">{$_('common.loading')}</p>
              </div>
            {:else if nonWorkingDays.length === 0}
              <div class="text-center py-8">
                <Calendar size={48} class="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                <p class="text-gray-500 dark:text-gray-400">{$_('settings.nonWorkingDays.empty')}</p>
              </div>
            {:else}
              <ul class="divide-y divide-gray-200 dark:divide-gray-700">
                {#each nonWorkingDays as day (day.date)}
                  <li class="flex items-center justify-between py-3 text-sm">
                    <div class="flex items-center space-x-3">
                      <span class="font-medium text-gray-900 dark:text-white">{day.date}</span>
                      <span class="text-gray-600 dark:text-gray-300">{day.label}</span>
                      <span class="px-2 py-0.5 text-xs rounded-full {day.kind === 'holiday' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'}">
                        {$_(`settings.nonWorkingDays.kinds.${day.kind}`)}
                      </span>
                    </div>
                    <button
                      type="button"
                      class="p-1.5 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      onclick={() => removeNonWorkingDay(day.date)}
                      disabled={deletingNonWorkingDay === day.date}
                      title={$_('common.delete')}
                    >
                      {#if deletingNonWorkingDay === day.date}
                        <Loader size={14} class="animate-spin" />
                      {:else}
                        <Trash2 size={14} />
                      {/if}
                    </button>
                  </li>
                {/each}
              </ul>
            {/if}
          </div>
        </div>
      {/if}

      <!-- Preferences Tab -->
      {#if activeTab === 'preferences'}
        <div class="card p-6">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">{$_('settings.preferences.title')}</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">{$_('settings.preferences.subtitle')}</p>

          <!-- Language Section -->
          <div class="mb-6">
            <label class="label flex items-center gap-2">
              <Globe size={16} class="text-gray-500" />
              {$_('settings.preferences.language')}
            </label>
            <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">{$_('settings.preferences.languageHint')}</p>

            <div class="flex gap-3">
              {#each SUPPORTED_LOCALES as lang}
                <button
                  class="flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all
                    {$locale === lang
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-700 dark:text-gray-300'
                    }"
                  onclick={() => setLocale(lang)}
                >
                  <span class="text-xl">{languageFlags[lang]}</span>
                  <span class="font-medium">{languageLabels[lang]}</span>
                  {#if $locale === lang}
                    <CheckCircle size={16} class="text-primary-500 ml-1" />
                  {/if}
                </button>
              {/each}
            </div>
          </div>
        </div>
      {/if}
    {/if}
  </div>
</Layout>
